<script setup lang="ts">
// Page admin V2 « Supervision des jobs (Cronicle) » — réservée DEV_ADMIN.
// Liste les events Cronicle (filtrables par environnement), affiche le dernier
// statut + ceux en cours, permet de consulter l'historique et de lancer un job.
// Accès : DEV_ADMIN (guard router `requiresDevAdmin` + endpoints back).
import { computed, onMounted, ref } from 'vue'
import {
  ArrowPathIcon,
  CheckCircleIcon,
  ClockIcon,
  ExclamationTriangleIcon,
  MinusCircleIcon,
  PlayIcon,
  XCircleIcon,
} from '@heroicons/vue/24/outline'
import {
  useCronicleStore,
  type CronicleEnv,
  type CronicleEnvFilter,
  type CronicleHistoryRow,
  type CronicleJob,
} from '@/v2/stores/useCronicleStore'

const store = useCronicleStore()

// ── Filtre environnement ──────────────────────────────────────────────────────
const ENV_TABS: { value: CronicleEnvFilter; label: string }[] = [
  { value: 'ALL', label: 'Tous' },
  { value: 'PRD', label: 'Production' },
  { value: 'REC', label: 'Recette' },
  { value: 'DEV', label: 'Dev' },
  { value: 'OTHER', label: 'Autres' },
]
const envFilter = ref<CronicleEnvFilter>('ALL')

async function selectEnv(env: CronicleEnvFilter) {
  envFilter.value = env
  await store.fetchJobs(env)
}

function refresh() {
  return store.fetchJobs(envFilter.value)
}

onMounted(refresh)

// ── Badges d'environnement ────────────────────────────────────────────────────
const ENV_BADGES: Record<CronicleEnv, { label: string; cls: string }> = {
  PRD: { label: 'PRD', cls: 'badge-error' },
  REC: { label: 'REC', cls: 'badge-warning' },
  DEV: { label: 'DEV', cls: 'badge-info' },
  OTHER: { label: 'Autre', cls: 'badge-ghost' },
}

// ── Helpers d'affichage ───────────────────────────────────────────────────────
const dateTimeFmt = new Intl.DateTimeFormat('fr-FR', { dateStyle: 'short', timeStyle: 'short' })

/** Epoch secondes → date locale. */
function formatEpoch(sec: number | null | undefined): string {
  return sec ? dateTimeFmt.format(new Date(sec * 1000)) : '—'
}

function formatDuration(sec: number | null | undefined): string {
  if (sec == null) return ''
  if (sec < 60) return `${Math.round(sec)} s`
  const m = Math.floor(sec / 60)
  const s = Math.round(sec % 60)
  return s ? `${m} min ${s} s` : `${m} min`
}

// ── Lancement d'un job (avec confirmation) ────────────────────────────────────
const confirmDialog = ref<HTMLDialogElement | null>(null)
const jobToRun = ref<CronicleJob | null>(null)
const feedback = ref<{ kind: 'success' | 'error'; text: string } | null>(null)

function askRun(job: CronicleJob) {
  jobToRun.value = job
  confirmDialog.value?.showModal()
}

async function confirmRun() {
  const job = jobToRun.value
  confirmDialog.value?.close()
  if (!job) return
  feedback.value = null
  const res = await store.runJob(job.id)
  if (res.ok) {
    feedback.value = { kind: 'success', text: `Job « ${job.title} » lancé.` }
    // Laisse à Cronicle le temps de démarrer le job avant de rafraîchir l'état.
    setTimeout(refresh, 1500)
  } else {
    feedback.value = { kind: 'error', text: res.message ?? 'Échec du lancement.' }
  }
  jobToRun.value = null
}

// ── Historique d'un job ───────────────────────────────────────────────────────
const historyDialog = ref<HTMLDialogElement | null>(null)
const historyJob = ref<CronicleJob | null>(null)
const historyRows = ref<CronicleHistoryRow[]>([])
const isLoadingHistory = ref(false)

async function openHistory(job: CronicleJob) {
  historyJob.value = job
  historyRows.value = []
  isLoadingHistory.value = true
  historyDialog.value?.showModal()
  historyRows.value = await store.fetchHistory(job.id, 20)
  isLoadingHistory.value = false
}

const runningCount = computed(() => store.jobs.filter((j) => j.running).length)
</script>

<template>
  <div class="mx-auto w-full max-w-7xl p-4 md:p-8">
    <header>
      <h1 class="text-2xl font-bold">Supervision des jobs (Cronicle)</h1>
      <p class="mt-1 max-w-prose text-base-content/80">
        Jobs planifiés sur Cronicle : dernier statut, exécutions en cours, et lancement à la demande.
        Filtrez par environnement et consultez l'historique de chaque job.
      </p>
    </header>

    <!-- ── Barre de filtres + rafraîchir ─────────────────────────────────────── -->
    <div class="mt-6 flex flex-wrap items-center justify-between gap-3">
      <div role="tablist" class="tabs tabs-boxed">
        <button
          v-for="tab in ENV_TABS"
          :key="tab.value"
          role="tab"
          type="button"
          class="tab"
          :class="{ 'tab-active': envFilter === tab.value }"
          :aria-selected="envFilter === tab.value"
          @click="selectEnv(tab.value)"
        >
          {{ tab.label }}
        </button>
      </div>

      <div class="flex items-center gap-2">
        <span v-if="runningCount" class="badge badge-info gap-1 tabular-nums">
          <span class="loading loading-spinner loading-xs" aria-hidden="true"></span>
          {{ runningCount }} en cours
        </span>
        <button
          type="button"
          class="btn btn-ghost"
          :disabled="store.isLoading"
          :aria-busy="store.isLoading"
          @click="refresh"
        >
          <ArrowPathIcon
            class="h-5 w-5"
            :class="{ 'animate-spin motion-reduce:animate-none': store.isLoading }"
            aria-hidden="true"
          />
          Rafraîchir
        </button>
      </div>
    </div>

    <!-- ── Feedback lancement ────────────────────────────────────────────────── -->
    <div
      v-if="feedback"
      role="alert"
      class="alert mt-4"
      :class="feedback.kind === 'success' ? 'alert-success' : 'alert-error'"
    >
      <CheckCircleIcon v-if="feedback.kind === 'success'" class="h-5 w-5 shrink-0" aria-hidden="true" />
      <ExclamationTriangleIcon v-else class="h-5 w-5 shrink-0" aria-hidden="true" />
      <span>{{ feedback.text }}</span>
    </div>

    <!-- ── Liste des jobs ────────────────────────────────────────────────────── -->
    <section class="card mt-4 border border-base-300 bg-base-100 shadow-sm">
      <div class="card-body p-4 md:p-6">
        <div v-if="store.error" role="alert" class="alert alert-error">
          <ExclamationTriangleIcon class="h-5 w-5 shrink-0" aria-hidden="true" />
          <span>{{ store.error }}</span>
        </div>

        <div v-if="store.isLoading && !store.jobs.length" class="py-8 text-center">
          <span class="loading loading-spinner" aria-hidden="true"></span>
          <p class="mt-2 text-base-content/80">Chargement des jobs…</p>
        </div>

        <p v-else-if="!store.jobs.length && !store.error" class="py-8 text-center text-base-content/80">
          Aucun job pour ce filtre.
        </p>

        <template v-else>
          <!-- Tableau (≥ md) -->
          <div class="-mx-4 hidden overflow-x-auto md:mx-0 md:block">
            <table class="table">
              <thead>
                <tr>
                  <th scope="col">Job</th>
                  <th scope="col">Env.</th>
                  <th scope="col">État</th>
                  <th scope="col">Dernier statut</th>
                  <th scope="col">Dernière exécution</th>
                  <th scope="col"><span class="sr-only">Actions</span></th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="job in store.jobs" :key="job.id">
                  <td>
                    <div class="flex items-center gap-2">
                      <span v-if="job.running" class="loading loading-spinner loading-xs text-info" aria-label="En cours"></span>
                      <span class="font-medium">{{ job.title }}</span>
                    </div>
                  </td>
                  <td>
                    <span class="badge badge-sm" :class="ENV_BADGES[job.env].cls">{{ ENV_BADGES[job.env].label }}</span>
                  </td>
                  <td>
                    <span class="badge badge-sm" :class="job.enabled ? 'badge-success' : 'badge-ghost'">
                      {{ job.enabled ? 'Activé' : 'Désactivé' }}
                    </span>
                  </td>
                  <td>
                    <span v-if="!job.lastRun" class="flex items-center gap-1 text-base-content/60">
                      <MinusCircleIcon class="h-5 w-5" aria-hidden="true" /> Jamais
                    </span>
                    <span
                      v-else
                      class="flex items-center gap-1"
                      :class="job.lastRun.success ? 'text-success' : 'text-error'"
                      :title="job.lastRun.description ?? ''"
                    >
                      <CheckCircleIcon v-if="job.lastRun.success" class="h-5 w-5 shrink-0" aria-hidden="true" />
                      <XCircleIcon v-else class="h-5 w-5 shrink-0" aria-hidden="true" />
                      {{ job.lastRun.success ? 'Succès' : `Échec (${job.lastRun.code})` }}
                    </span>
                  </td>
                  <td class="whitespace-nowrap tabular-nums">
                    {{ formatEpoch(job.lastRun?.startedAt) }}
                    <span v-if="job.lastRun?.elapsedSec != null" class="block text-xs text-base-content/60">
                      {{ formatDuration(job.lastRun.elapsedSec) }}
                    </span>
                  </td>
                  <td>
                    <div class="flex justify-end gap-1">
                      <button
                        type="button"
                        class="btn btn-ghost btn-sm"
                        :aria-label="`Historique de ${job.title}`"
                        title="Historique"
                        @click="openHistory(job)"
                      >
                        <ClockIcon class="h-5 w-5" aria-hidden="true" />
                      </button>
                      <button
                        type="button"
                        class="btn btn-primary btn-sm gap-1"
                        :disabled="store.runningId === job.id"
                        :aria-busy="store.runningId === job.id"
                        @click="askRun(job)"
                      >
                        <span v-if="store.runningId === job.id" class="loading loading-spinner loading-xs" aria-hidden="true"></span>
                        <PlayIcon v-else class="h-4 w-4" aria-hidden="true" />
                        Lancer
                      </button>
                    </div>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          <!-- Cartes (mobile < md) -->
          <ul role="list" class="flex flex-col gap-2 md:hidden">
            <li v-for="job in store.jobs" :key="job.id" class="rounded-lg border border-base-200 p-3">
              <div class="flex items-start justify-between gap-2">
                <div class="flex min-w-0 items-center gap-2">
                  <span v-if="job.running" class="loading loading-spinner loading-xs text-info" aria-label="En cours"></span>
                  <span class="font-medium">{{ job.title }}</span>
                </div>
                <span class="badge badge-sm shrink-0" :class="ENV_BADGES[job.env].cls">{{ ENV_BADGES[job.env].label }}</span>
              </div>
              <div class="mt-2 flex flex-wrap items-center gap-2 text-sm">
                <span class="badge badge-sm" :class="job.enabled ? 'badge-success' : 'badge-ghost'">
                  {{ job.enabled ? 'Activé' : 'Désactivé' }}
                </span>
                <span v-if="!job.lastRun" class="text-base-content/60">Jamais exécuté</span>
                <span v-else :class="job.lastRun.success ? 'text-success' : 'text-error'">
                  {{ job.lastRun.success ? 'Succès' : `Échec (${job.lastRun.code})` }} · {{ formatEpoch(job.lastRun.startedAt) }}
                </span>
              </div>
              <div class="mt-2 flex items-center gap-1 border-t border-base-200 pt-2">
                <button type="button" class="btn btn-ghost btn-sm gap-1" @click="openHistory(job)">
                  <ClockIcon class="h-4 w-4" aria-hidden="true" /> Historique
                </button>
                <button
                  type="button"
                  class="btn btn-primary btn-sm gap-1"
                  :disabled="store.runningId === job.id"
                  :aria-busy="store.runningId === job.id"
                  @click="askRun(job)"
                >
                  <PlayIcon class="h-4 w-4" aria-hidden="true" /> Lancer
                </button>
              </div>
            </li>
          </ul>
        </template>
      </div>
    </section>

    <!-- ── Modal confirmation lancement ──────────────────────────────────────── -->
    <dialog ref="confirmDialog" class="modal">
      <div class="modal-box">
        <h3 class="text-lg font-semibold">Lancer le job ?</h3>
        <p class="mt-2 text-base-content/80">
          Vous êtes sur le point de déclencher
          <span class="font-medium text-base-content">{{ jobToRun?.title }}</span>
          <template v-if="jobToRun"> (<span :class="jobToRun.env === 'PRD' ? 'font-semibold text-error' : ''">{{ ENV_BADGES[jobToRun.env].label }}</span>)</template>.
        </p>
        <div v-if="jobToRun?.env === 'PRD'" role="alert" class="alert alert-warning mt-3">
          <ExclamationTriangleIcon class="h-5 w-5 shrink-0" aria-hidden="true" />
          <span>Ce job s'exécute en <strong>production</strong>.</span>
        </div>
        <div class="modal-action">
          <button type="button" class="btn btn-primary" @click="confirmRun">
            <PlayIcon class="h-5 w-5" aria-hidden="true" /> Lancer
          </button>
          <form method="dialog">
            <button class="btn btn-ghost">Annuler</button>
          </form>
        </div>
      </div>
      <form method="dialog" class="modal-backdrop">
        <button>Fermer</button>
      </form>
    </dialog>

    <!-- ── Modal historique ──────────────────────────────────────────────────── -->
    <dialog ref="historyDialog" class="modal">
      <div class="modal-box max-w-2xl">
        <h3 class="text-lg font-semibold">Historique — {{ historyJob?.title }}</h3>

        <div v-if="isLoadingHistory" class="py-8 text-center">
          <span class="loading loading-spinner" aria-hidden="true"></span>
          <p class="mt-2 text-base-content/80">Chargement…</p>
        </div>

        <p v-else-if="!historyRows.length" class="py-8 text-center text-base-content/80">
          Aucune exécution enregistrée.
        </p>

        <div v-else class="mt-3 max-h-96 overflow-y-auto overflow-x-auto">
          <table class="table table-sm">
            <thead class="sticky top-0 bg-base-100">
              <tr>
                <th scope="col">Démarré</th>
                <th scope="col">Statut</th>
                <th scope="col" class="text-right">Durée</th>
                <th scope="col">Détail</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="row in historyRows" :key="row.id">
                <td class="whitespace-nowrap tabular-nums">{{ formatEpoch(row.time_start) }}</td>
                <td>
                  <span class="badge badge-sm" :class="row.code === 0 ? 'badge-success' : 'badge-error'">
                    {{ row.code === 0 ? 'Succès' : `Échec (${row.code})` }}
                  </span>
                </td>
                <td class="text-right tabular-nums">{{ formatDuration(row.elapsed) || '—' }}</td>
                <td class="max-w-xs truncate" :title="row.description ?? ''">{{ row.description ?? '—' }}</td>
              </tr>
            </tbody>
          </table>
        </div>

        <div class="modal-action">
          <form method="dialog">
            <button class="btn btn-ghost">Fermer</button>
          </form>
        </div>
      </div>
      <form method="dialog" class="modal-backdrop">
        <button>Fermer</button>
      </form>
    </dialog>
  </div>
</template>
